import NProgress from "nprogress";
import { toast } from "vue-sonner";
import {
  DEFAULT_AUTHENTICATED_PATH,
  LOGIN_PATH,
  PERMISSION_ROUTES_TTL,
  USER_INFO_TTL,
} from "./constants";
import { isDynamicRoutesReady, markDynamicRoutesReady, resetDynamicRoutes } from "./dynamic-state";
import { addDynamicRoutes, removeDynamicRoutes } from "./dynamic-routes";
import router from "./index";
import { usePermissionStore } from "@/entities/permission/store";
import { useUserStore } from "@/entities/user/store";
import { usePageTabsStore } from "@/shared/store/page-tabs";
import { useRouteCacheStore } from "@/shared/store/route-cache";
import "nprogress/nprogress.css";

NProgress.configure({ showSpinner: false });

router.beforeEach(async (to) => {
  if (to.meta.public) {
    return true;
  }

  NProgress.start();

  const userStore = useUserStore();
  const permissionStore = usePermissionStore();

  if (!userStore.token) {
    if (permissionStore.whiteListRouters.includes(to.path)) {
      return true;
    }

    return {
      path: LOGIN_PATH,
      query: { redirect: encodeURIComponent(to.fullPath) },
    };
  }

  if (to.path === LOGIN_PATH) {
    return DEFAULT_AUTHENTICATED_PATH;
  }

  try {
    await userStore.refreshUserInfoIfStale(USER_INFO_TTL);

    if (!isDynamicRoutesReady() || permissionStore.shouldRefreshRoutes(PERMISSION_ROUTES_TTL)) {
      const routeList = await permissionStore.buildAsyncRoutes();

      // Keep the current menu visible while refreshing permissions. Replacing the
      // registered routes only after the request succeeds avoids unmounting the
      // sidebar during every TTL refresh.
      removeDynamicRoutes(router);
      addDynamicRoutes(router, routeList);

      const availableRoutes = router.getRoutes().flatMap((routeRecord) => {
        const name = routeRecord.name;

        if (typeof name !== "string") {
          return [];
        }

        const componentKey = routeRecord.meta.routeComponentKey;
        return [
          {
            name,
            signature: `${name}:${typeof componentKey === "string" ? componentKey : "UNKNOWN_COMPONENT"}`,
            keepAlive: routeRecord.meta.keepAlive === true,
          },
        ];
      });
      const pageTabsStore = usePageTabsStore();
      pageTabsStore.normalizeTabs();
      const availableTabRoutes = pageTabsStore.tabs.flatMap((tab) => {
        const resolvedTabRoute = router.resolve(tab.fullPath);
        const isAvailable = resolvedTabRoute.name === tab.name;
        return isAvailable && tab.name
          ? [{ name: tab.name, route: resolvedTabRoute, tabKey: tab.key }]
          : [];
      });
      pageTabsStore.retainRouteNames(availableTabRoutes.map(({ name }) => name));
      const routeCacheStore = useRouteCacheStore();
      await routeCacheStore.retainRouteDefinitions(availableRoutes);
      availableTabRoutes.forEach(({ route, tabKey }) => {
        routeCacheStore.registerRoute(route, tabKey);
      });

      markDynamicRoutesReady();

      const refreshedTarget = router.resolve(to.fullPath);
      if (to.name !== "NotFound" && refreshedTarget.name === "NotFound") {
        return { path: DEFAULT_AUTHENTICATED_PATH, replace: true };
      }

      return {
        path: to.path,
        query: to.query,
        hash: to.hash,
        replace: true,
      };
    }

    return true;
  } catch (error) {
    const message = error instanceof Error ? error.message : "登录状态已失效";
    toast.error(message);
    userStore.logout();
    usePageTabsStore().clearTabs();
    permissionStore.restoreRoutes();
    removeDynamicRoutes(router);
    resetDynamicRoutes();

    return {
      path: LOGIN_PATH,
      query: { redirect: encodeURIComponent(to.fullPath) },
    };
  }
});

router.afterEach((to) => {
  if (!to.meta.public) {
    usePageTabsStore().addRouteTab(to);
  }
  useRouteCacheStore().completeRouteInvalidations();
  NProgress.done();
});

router.onError(() => {
  useRouteCacheStore().completeRouteInvalidations();
  NProgress.done();
});
